import React, { useState } from "react";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import { ReactComponent as LinkedinIcon } from "../../../assets/svg/linkedInLogoSvg.svg";
import { ReactComponent as ClassicLinkedinIcon } from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/svg/linkedIn.svg";
import { ReactComponent as LinkedinPremiumIcon } from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/svg/liPremiumIcon.svg";
import CustomRadioButton from "../common/customRadioBtn";
import Button from "../common/Button";
import linkedInUtils from "Pages/Dashboard/settings/ConnectedAccounts/LinkedInAccounts/common/linkedInUtils";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "../common/toaster";

class AddLinkedInAccountPopupState {
  @observable showPopup = false;
  @observable cbk = () => {};
  @observable message = "";

  @action
  setShowPopup = (value = false) => {
    this.showPopup = value;
  };
}

const addLinkedInAccountPopupState = new AddLinkedInAccountPopupState();

const AddLinkedInAccountPopup = observer(() => {
  const [view, setView] = useState("chooseType");
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [selectedAccountType, setSelectedAccountType] =
    useState("salesNavigator");

  if (!addLinkedInAccountPopupState?.showPopup) {
    return null;
  }

  const closePopup = () => {
    addLinkedInAccountPopupState.setShowPopup(false);
    setView("chooseType");
    setInviteEmail("");
    setSendingInvite(false);
    setSelectedAccountType("salesNavigator");
  };

  const handleConnectOAuth = () => {
    linkedInUtils.connectViaOAuth();
    closePopup();
  };

  const handleTryAnotherWay = () => {
    setView("chooseExtensionType");
  };

  const handleAccountTypeRadioChange = (e, id) => {
    setSelectedAccountType(id);
  };

  const handleContinueExtensionType = () => {
    linkedInUtils.connectViaExtension(selectedAccountType !== "standard");
    closePopup();
  };

  const handleSendInvite = async () => {
    if (!inviteEmail?.trim() || sendingInvite) {
      return;
    }
    setSendingInvite(true);
    const config = {
      url: URLS.sendLinkedInConnectInvite,
      method: "POST",
      data: { toAddress: inviteEmail.trim() }
    };
    const response = await makeApi(config);
    setSendingInvite(false);
    if (response?.data?.status) {
      setView("inviteSent");
    } else {
      toasterState.setToastMsg(
        "Something went wrong while sending the invite. Please try again.",
        "fail"
      );
    }
  };

  const renderChooseType = () => (
    <>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Connect LinkedIn</div>
          <div className="chooseAccountType">
            Authorize your LinkedIn account to get started
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInAccountPopupFooter">
          <Button
            id="continueLinkedInBtn"
            name="continueLinkedInBtn"
            btnText="Connect LinkedIn"
            className="continueLinkedInBtn"
            type="button"
            click={handleConnectOAuth}
          />
        </div>
        <div className="sendInviteDivider">
          <span>or</span>
        </div>
        <div
          className="sendInviteInsteadLink"
          onClick={() => setView("invite")}
        >
          Send an invite instead
        </div>
        <div className="tryAnotherWayTrigger" onClick={handleTryAnotherWay}>
          <span className="tryAnotherWayLabel">
            Having trouble connecting your account?
          </span>{" "}
          <span className="tryAnotherWayLink">Try another way</span>
        </div>
      </div>
    </>
  );

  const renderChooseExtensionType = () => (
    <>
      <div
        className="linkedInInviteBackLink"
        onClick={() => setView("chooseType")}
      >
        <i className="material-icons">arrow_back</i>
        Back
      </div>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Connect LinkedIn</div>
          <div className="chooseAccountType">
            Choose the account type you want to connect
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInAccountOptions">
          <div
            className={`linkedInAccountOption ${
              selectedAccountType === "standard" ? "selected" : ""
            }`}
            onClick={() => setSelectedAccountType("standard")}
          >
            <CustomRadioButton
              name="linkedInAccountType"
              id="standard"
              value=""
              checked={selectedAccountType === "standard"}
              radioOnchange={handleAccountTypeRadioChange}
            />
            <span className="optionIcon">
              <ClassicLinkedinIcon />
            </span>
            <div className="optionContent">
              <div className="optionTitle">Standard/ Premium LinkedIn</div>
              <div className="optionDescription">
                Automate profile views, requests, and messages.
              </div>
            </div>
          </div>
          <div
            className={`linkedInAccountOption ${
              selectedAccountType === "salesNavigator" ? "selected" : ""
            }`}
            onClick={() => setSelectedAccountType("salesNavigator")}
          >
            <CustomRadioButton
              name="linkedInAccountType"
              id="salesNavigator"
              value=""
              checked={selectedAccountType === "salesNavigator"}
              radioOnchange={handleAccountTypeRadioChange}
            />
            <span className="optionIcon">
              <LinkedinPremiumIcon />
            </span>
            <div className="optionContent">
              <div className="optionTitle">
                Sales Navigator{" "}
                <span className="recommended">(recommended)</span>
              </div>
              <div className="optionDescription">
                Unlock contact imports, advanced workflows, and higher
                automation limits.
              </div>
            </div>
          </div>
        </div>
        <div className="linkedInAccountPopupFooter">
          <Button
            id="continueLinkedInBtn"
            name="continueLinkedInBtn"
            btnText="Continue"
            className="continueLinkedInBtn"
            type="button"
            click={handleContinueExtensionType}
          />
        </div>
      </div>
    </>
  );

  const renderInvite = () => (
    <>
      <div
        className="linkedInInviteBackLink"
        onClick={() => setView("chooseType")}
      >
        <i className="material-icons">arrow_back</i>
        Back
      </div>
      <div className="commonPopupHeader linkedInAccountPopupHeader">
        <div className="linkedinIcon">
          <LinkedinIcon />
        </div>
        <div className="linkedinHeadingWrapper">
          <div className="headingTitle">Send invite link</div>
          <div className="chooseAccountType">
            We'll email a one-time LinkedIn connect link to this address
          </div>
        </div>
      </div>
      <div className="commonPopupBody linkedInAccountPopupBody">
        <div className="linkedInInviteEmailField">
          <label htmlFor="linkedInInviteEmail">Email address</label>
          <input
            id="linkedInInviteEmail"
            type="email"
            placeholder="teammate@company.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
          />
        </div>
        <div className="linkedInAccountPopupFooter">
          <Button
            id="sendLinkedInInviteBtn"
            name="sendLinkedInInviteBtn"
            btnText="Send invite"
            className={`continueLinkedInBtn${
              !inviteEmail?.trim() ? " disabled" : ""
            }${sendingInvite ? " loading" : ""}`}
            type="button"
            loader={sendingInvite}
            click={handleSendInvite}
          />
        </div>
      </div>
    </>
  );

  const renderInviteSent = () => (
    <div className="linkedInInviteSent">
      <i className="material-icons">check_circle</i>
      <div className="headingTitle">Invite sent</div>
      <div className="chooseAccountType">
        We emailed the connect link to {inviteEmail}. It'll show up as
        pending until they finish authorizing.
      </div>
      <div className="linkedInAccountPopupFooter">
        <Button
          id="doneLinkedInInviteBtn"
          name="doneLinkedInInviteBtn"
          btnText="Done"
          className="continueLinkedInBtn"
          type="button"
          click={closePopup}
        />
      </div>
    </div>
  );

  return (
    <div className="commonPopupWrapper linkedInAccountPopupWrapper">
      <div className="common-popup-mask" />
      <div className="commonPopupContainer linkedInAccountPopupContainer animated customzoomIn">
        <div className="closeIconContainer">
          <i
            className="material-icons commonPopupCloseIcon"
            onClick={closePopup}
          >
            close
          </i>
        </div>
        {view === "chooseType" && renderChooseType()}
        {view === "chooseExtensionType" && renderChooseExtensionType()}
        {view === "invite" && renderInvite()}
        {view === "inviteSent" && renderInviteSent()}
      </div>
    </div>
  );
});

export { addLinkedInAccountPopupState };
export default AddLinkedInAccountPopup;
